import type { ShopThread } from '@pc/db';

export interface ThreadSnap {
  unread: number;
  lastMessageAt: string;
}

export function snapshotShopThreads(
  threads: ShopThread[],
  side: 'buyer' | 'vendor',
): Map<string, ThreadSnap> {
  const map = new Map<string, ThreadSnap>();
  for (const t of threads) {
    map.set(t.id, {
      unread: side === 'buyer' ? t.buyer_unread_count : t.vendor_unread_count,
      lastMessageAt: t.last_message_at,
    });
  }
  return map;
}

export interface ShopMessageNotification {
  threadId: string;
  vendorId: string;
  lastMessageAt: string;
}

/** Compare thread list to a prior snapshot; skip the first poll (baseline). */
export function detectNewShopMessageThreads(
  threads: ShopThread[],
  prev: Map<string, ThreadSnap> | null,
  side: 'buyer' | 'vendor',
  opts: { activeThreadId?: string | null },
): { baseline: boolean; notifications: ShopMessageNotification[] } {
  if (!prev) {
    return { baseline: true, notifications: [] };
  }

  const notifications: ShopMessageNotification[] = [];

  for (const t of threads) {
    if (t.id === opts.activeThreadId) continue;

    const unread =
      side === 'buyer' ? t.buyer_unread_count : t.vendor_unread_count;
    if (unread <= 0) continue;

    const p = prev.get(t.id);
    if (!p) {
      notifications.push({
        threadId: t.id,
        vendorId: t.vendor_id,
        lastMessageAt: t.last_message_at,
      });
      continue;
    }

    const messageAdvanced = t.last_message_at > p.lastMessageAt;
    const unreadIncreased = unread > p.unread;
    if (messageAdvanced || unreadIncreased) {
      notifications.push({
        threadId: t.id,
        vendorId: t.vendor_id,
        lastMessageAt: t.last_message_at,
      });
    }
  }

  return { baseline: false, notifications };
}
